import React from 'react';
import { Box, Typography } from '@mui/material';
import { Code, CodeLinks, Field, Section } from './DetailPrimitives';
import ConceptParametersSection from './ConceptParametersSection';
import UsedByTemplatesSection from './UsedByTemplatesSection';
import { OntologyKind, conceptHref, policyHref } from './ontologyFormat';
import { dsVar } from '../../theme/tokens';

/**
 * The BLAST RADIUS of one ontology record: everything a change to it would reach.
 *
 * The detail pages had these findings scattered down a long scroll, interleaved with the
 * prose that explains what the record MEANS. That is the wrong pairing. A reader asking
 * "what breaks if I retune this?" is answered by a fixed set of small relational blocks —
 * the thresholds configured against it, the terms next to it, the agents running it — and
 * those belong together, beside the reading column and in view while the tuning and
 * threshold guidance that raises the question is on screen.
 *
 * Ordered NEAREST FIRST: what is configured on the record, then what sits beside it in
 * the vocabulary, then what runs it in production.
 *
 * TWO THINGS DELIBERATELY STAY IN THE MAIN COLUMN, despite belonging to the blast radius:
 *
 *   - "How this connects", the mesh diagram. It IS the blast radius, drawn — but a graph
 *     with 210px nodes is unreadable in a 22rem rail. It needs the width;
 *   - "Thresholds this policy sets", on a policy. Unlike the concept-side equivalent this
 *     is not a short list: TM.STANDARD governs 48 concepts, and the UNSET rows are the
 *     point of the section. It is the policy page's primary content, not a side note.
 *
 * Everything here self-hides when empty, so a sparse record gets a short rail rather than
 * a column of empty headings.
 */

const SCOPE_FIELD_LABEL: Record<string, string> = {
    activity_template_code: 'Activity',
    segment: 'Segment',
    product: 'Product',
    jurisdiction: 'Jurisdiction',
    alert_type: 'Alert type',
    flow_template_code: 'Flow',
};

const linkOrCode = (code: string | undefined, href: (c: string) => string | null) => {
    if (!code) return null;
    const target = href(code);
    return target ? (
        <Box
            component="a"
            href={target}
            sx={{
                fontFamily: '"JetBrains Mono", monospace', fontSize: '0.8125rem',
                color: dsVar('accent-fg'), textDecoration: 'none', wordBreak: 'break-word',
                '&:hover': { textDecoration: 'underline' },
            }}
        >
            {code}
        </Box>
    ) : <Code>{code}</Code>;
};

/** What a concept reaches: its configured thresholds, its neighbours, its agents. */
const ConceptReach: React.FC<{ document: Record<string, any> }> = ({ document }) => (
    <>
        {/* First because it is the one block whose values a reader may be about to
            change — it sits directly across from Tuning's recommended range. */}
        <ConceptParametersSection conceptCode={document.concept_code} />

        {Array.isArray(document.relations) && document.relations.length > 0 && (
            <Section title="Related concepts">
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    {document.relations.map((relation: any, index: number) => {
                        const href = conceptHref(relation.concept_code);
                        return (
                            <Box key={index} sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                <Typography
                                    sx={{ flexShrink: 0, fontSize: '0.75rem', color: dsVar('fg-muted'), minWidth: 62 }}
                                >
                                    {relation.type}
                                </Typography>
                                {href ? (
                                    <Box
                                        component="a"
                                        href={href}
                                        sx={{
                                            fontFamily: '"JetBrains Mono", monospace', fontSize: '0.8125rem',
                                            color: dsVar('accent-fg'), textDecoration: 'none', wordBreak: 'break-word',
                                            '&:hover': { textDecoration: 'underline' },
                                        }}
                                    >
                                        {relation.concept_code}
                                    </Box>
                                ) : (
                                    <Code>{relation.concept_code}</Code>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            </Section>
        )}

        {/* On a typology: the signals that evidence it. */}
        {Array.isArray(document.detected_by) && document.detected_by.length > 0 && (
            <Section title="Detected by">
                <CodeLinks codes={document.detected_by} href={conceptHref} />
            </Section>
        )}
    </>
);

/**
 * What a parameter reaches. The binding is the substance of a threshold and is easy to
 * get wrong: the engine matches on appliesToPath, then on parameterCode equalling a
 * config key, then on conceptCode — so naming a parameter after a threshold does not make
 * it apply. Scope narrows that reach further, which is why the two read as one panel.
 */
const ParameterReach: React.FC<{ document: Record<string, any> }> = ({ document }) => {
    const scope = document.scope || {};
    const declaredScope = Object.entries(scope).filter(([, value]) => value);

    return (
        <>
            <Section title="What it governs">
                <Field label="Concept" value={linkOrCode(document.concept_code, conceptHref)} />
                <Field label="Policy" value={linkOrCode(document.policy_code, policyHref)} />
                {/* The only binding form that can target one leaf of a MULTICOLUMN row. */}
                <Field
                    label="Configuration path"
                    value={document.applies_to_path && (
                        <Box component="span" sx={{ wordBreak: 'break-all' }}>
                            <Code>{document.applies_to_path}</Code>
                        </Box>
                    )}
                />
            </Section>

            <Section title="Where it applies" always>
                {declaredScope.length === 0 ? (
                    <Typography sx={{ fontSize: '0.875rem', color: dsVar('fg-2') }}>
                        Everywhere — this parameter declares no scope, so it applies to anything not
                        covered by something more specific.
                    </Typography>
                ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {declaredScope.map(([field, value]) => (
                            <Box
                                key={field}
                                sx={{
                                    px: 1, py: 0.5, borderRadius: '4px',
                                    border: `1px solid ${dsVar('line')}`, backgroundColor: dsVar('page'),
                                }}
                            >
                                <Typography component="span" sx={{ fontSize: '0.6875rem', color: dsVar('fg-muted'), mr: 0.75 }}>
                                    {SCOPE_FIELD_LABEL[field] ?? field}
                                </Typography>
                                <Typography
                                    component="span"
                                    sx={{
                                        fontFamily: '"JetBrains Mono", monospace',
                                        fontSize: '0.75rem', color: dsVar('fg-soft'), wordBreak: 'break-all',
                                    }}
                                >
                                    {String(value)}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                )}
            </Section>
        </>
    );
};

const OntologyReachRail: React.FC<{
    kind: OntologyKind;
    code: string;
    document: Record<string, any>;
}> = ({ kind, code, document }) => (
    <>
        {kind === 'concepts' && <ConceptReach document={document} />}
        {kind === 'parameters' && <ParameterReach document={document} />}

        {/* Last on every kind: the furthest reach, and the only block common to all three. */}
        <UsedByTemplatesSection kind={kind} code={code} />
    </>
);

export default OntologyReachRail;
